import { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, Globe, Building2, Map, Hash, Plus, Edit2, Trash2,
  Search, ChevronRight, Upload, Download, RefreshCw, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LocationSettingsPage = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('states');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ states: 0, districts: 0, cities: 0, pincodes: 0 });
  
  // States
  const [states, setStates] = useState([]);
  const [stateSearch, setStateSearch] = useState('');
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [stateForm, setStateForm] = useState({ name: '', code: '', is_active: true });
  
  // Districts
  const [districts, setDistricts] = useState([]);
  const [districtSearch, setDistrictSearch] = useState('');
  const [selectedStateForDistrict, setSelectedStateForDistrict] = useState('all');
  const [districtPage, setDistrictPage] = useState(1);
  const [districtTotal, setDistrictTotal] = useState(0);
  const [districtDialogOpen, setDistrictDialogOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [districtForm, setDistrictForm] = useState({ name: '', state_id: '', is_active: true });
  
  // Cities
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [selectedDistrictForCity, setSelectedDistrictForCity] = useState('all');
  const [cityPage, setCityPage] = useState(1);
  const [cityTotal, setCityTotal] = useState(0);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityForm, setCityForm] = useState({ name: '', district_id: '', is_active: true });
  
  // Pincodes
  const [pincodes, setPincodes] = useState([]);
  const [pincodeSearch, setPincodeSearch] = useState('');
  const [selectedCityForPincode, setSelectedCityForPincode] = useState('all');
  const [pincodePage, setPincodePage] = useState(1);
  const [pincodeTotal, setPincodeTotal] = useState(0);
  const [pincodeDialogOpen, setPincodeDialogOpen] = useState(false);
  const [editingPincode, setEditingPincode] = useState(null);
  const [pincodeForm, setPincodeForm] = useState({ pincode: '', area_name: '', city_id: '', is_active: true });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // Data Seeder
  const [seederDialogOpen, setSeederDialogOpen] = useState(false);
  const [seedingStates, setSeedingStates] = useState(false);
  const [seedingDistricts, setSeedingDistricts] = useState(false);
  const [seedingPincodes, setSeedingPincodes] = useState(false);
  const [seedProgress, setSeedProgress] = useState('');
  
  // Dropdown data
  const [statesDropdown, setStatesDropdown] = useState([]);
  const [districtsDropdown, setDistrictsDropdown] = useState([]);
  const [citiesDropdown, setCitiesDropdown] = useState([]);
  
  const getToken = () => localStorage.getItem('access_token');

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch states
  const fetchStates = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/locations/states?`;
      if (stateSearch) url += `search=${encodeURIComponent(stateSearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStates(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch states', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [stateSearch, toast]);

  // Fetch states dropdown
  const fetchStatesDropdown = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations/states/dropdown`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStatesDropdown(data);
    } catch (error) {
      console.error('Failed to fetch states dropdown:', error);
    }
  }, []);

  // Fetch districts
  const fetchDistricts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/locations/districts?page=${districtPage}&page_size=50`;
      if (selectedStateForDistrict !== 'all') url += `&state_id=${selectedStateForDistrict}`;
      if (districtSearch) url += `&search=${encodeURIComponent(districtSearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setDistricts(data.items || []);
      setDistrictTotal(data.total || 0);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch districts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [districtPage, selectedStateForDistrict, districtSearch, toast]);

  // Fetch districts dropdown
  const fetchDistrictsDropdown = useCallback(async (stateId) => {
    try {
      let url = `${API_URL}/api/locations/districts/dropdown`;
      if (stateId) url += `?state_id=${stateId}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setDistrictsDropdown(data);
    } catch (error) {
      console.error('Failed to fetch districts dropdown:', error);
    }
  }, []);

  // Fetch cities
  const fetchCities = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/locations/cities?page=${cityPage}&page_size=50`;
      if (selectedDistrictForCity !== 'all') url += `&district_id=${selectedDistrictForCity}`;
      if (citySearch) url += `&search=${encodeURIComponent(citySearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setCities(data.items || []);
      setCityTotal(data.total || 0);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch cities', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [cityPage, selectedDistrictForCity, citySearch, toast]);

  // Fetch cities dropdown
  const fetchCitiesDropdown = useCallback(async (districtId) => {
    try {
      let url = `${API_URL}/api/locations/cities/dropdown`;
      if (districtId) url += `?district_id=${districtId}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setCitiesDropdown(data);
    } catch (error) {
      console.error('Failed to fetch cities dropdown:', error);
    }
  }, []);

  // Fetch pincodes
  const fetchPincodes = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/locations/pincodes?page=${pincodePage}&page_size=50`;
      if (selectedCityForPincode !== 'all') url += `&city_id=${selectedCityForPincode}`;
      if (pincodeSearch) url += `&search=${encodeURIComponent(pincodeSearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setPincodes(data.items || []);
      setPincodeTotal(data.total || 0);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch pincodes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [pincodePage, selectedCityForPincode, pincodeSearch, toast]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchStatesDropdown();
  }, [fetchStats, fetchStatesDropdown]);

  // Tab-based data loading
  useEffect(() => {
    if (activeTab === 'states') fetchStates();
    else if (activeTab === 'districts') fetchDistricts();
    else if (activeTab === 'cities') fetchCities();
    else if (activeTab === 'pincodes') fetchPincodes();
  }, [activeTab, fetchStates, fetchDistricts, fetchCities, fetchPincodes]);

  // CRUD Operations
  const handleSaveState = async () => {
    try {
      const url = editingState 
        ? `${API_URL}/api/locations/states/${editingState.id}`
        : `${API_URL}/api/locations/states`;
      
      const response = await fetch(url, {
        method: editingState ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(stateForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save state');
      }
      
      toast({ title: 'Success', description: `State ${editingState ? 'updated' : 'created'} successfully` });
      setStateDialogOpen(false);
      setEditingState(null);
      setStateForm({ name: '', code: '', is_active: true });
      fetchStates();
      fetchStats();
      fetchStatesDropdown();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSaveDistrict = async () => {
    try {
      const url = editingDistrict 
        ? `${API_URL}/api/locations/districts/${editingDistrict.id}`
        : `${API_URL}/api/locations/districts`;
      
      const response = await fetch(url, {
        method: editingDistrict ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(districtForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save district');
      }
      
      toast({ title: 'Success', description: `District ${editingDistrict ? 'updated' : 'created'} successfully` });
      setDistrictDialogOpen(false);
      setEditingDistrict(null);
      setDistrictForm({ name: '', state_id: '', is_active: true });
      fetchDistricts();
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSaveCity = async () => {
    try {
      const url = editingCity 
        ? `${API_URL}/api/locations/cities/${editingCity.id}`
        : `${API_URL}/api/locations/cities`;
      
      const response = await fetch(url, {
        method: editingCity ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(cityForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save city');
      }
      
      toast({ title: 'Success', description: `City ${editingCity ? 'updated' : 'created'} successfully` });
      setCityDialogOpen(false);
      setEditingCity(null);
      setCityForm({ name: '', district_id: '', is_active: true });
      fetchCities();
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSavePincode = async () => {
    try {
      const url = editingPincode 
        ? `${API_URL}/api/locations/pincodes/${editingPincode.id}`
        : `${API_URL}/api/locations/pincodes`;
      
      const response = await fetch(url, {
        method: editingPincode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(pincodeForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save pincode');
      }
      
      toast({ title: 'Success', description: `Pincode ${editingPincode ? 'updated' : 'created'} successfully` });
      setPincodeDialogOpen(false);
      setEditingPincode(null);
      setPincodeForm({ pincode: '', area_name: '', city_id: '', is_active: true });
      fetchPincodes();
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      const endpoints = {
        state: 'states',
        district: 'districts',
        city: 'cities',
        pincode: 'pincodes'
      };
      
      const response = await fetch(`${API_URL}/api/locations/${endpoints[deleteType]}/${deleteItem.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: 'Success', description: `${deleteType} deactivated successfully` });
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      
      if (deleteType === 'state') fetchStates();
      else if (deleteType === 'district') fetchDistricts();
      else if (deleteType === 'city') fetchCities();
      else if (deleteType === 'pincode') fetchPincodes();
      
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const openEditState = (state) => {
    setEditingState(state);
    setStateForm({ name: state.name, code: state.code, is_active: state.is_active });
    setStateDialogOpen(true);
  };

  const openEditDistrict = (district) => {
    setEditingDistrict(district);
    setDistrictForm({ name: district.name, state_id: district.state_id, is_active: district.is_active });
    setDistrictDialogOpen(true);
  };

  const openEditCity = (city) => {
    setEditingCity(city);
    setCityForm({ name: city.name, district_id: city.district_id, is_active: city.is_active });
    fetchDistrictsDropdown(city.state_id);
    setCityDialogOpen(true);
  };

  const openEditPincode = (pincode) => {
    setEditingPincode(pincode);
    setPincodeForm({ 
      pincode: pincode.pincode, 
      area_name: pincode.area_name, 
      city_id: pincode.city_id, 
      is_active: pincode.is_active 
    });
    fetchCitiesDropdown(pincode.district_id);
    setPincodeDialogOpen(true);
  };

  const confirmDelete = (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  // Data Seeding Functions
  const seedStatesAndDistricts = async () => {
    try {
      setSeedingStates(true);
      setSeedProgress('Seeding states...');
      
      // Seed states
      const statesResponse = await fetch(`${API_URL}/api/locations/seed-india-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const statesResult = await statesResponse.json();
      
      setSeedProgress(`States: ${statesResult.states || statesResult.states_created || 'Done'}. Seeding districts...`);
      
      // Seed missing districts
      const districtsResponse = await fetch(`${API_URL}/api/locations/seed-missing-districts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const districtsResult = await districtsResponse.json();
      
      toast({ 
        title: 'Success', 
        description: `States and Districts seeded! Districts created: ${districtsResult.districts_created || 0}` 
      });
      
      fetchStats();
      fetchStates();
      fetchStatesDropdown();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to seed states/districts', variant: 'destructive' });
    } finally {
      setSeedingStates(false);
      setSeedProgress('');
    }
  };

  const seedAllPincodes = async () => {
    try {
      setSeedingPincodes(true);
      setSeedProgress('Starting pincode import... This may take 2-3 minutes.');
      
      const response = await fetch(`${API_URL}/api/locations/seed-all-pincodes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to seed pincodes');
      }
      
      const result = await response.json();
      
      toast({ 
        title: 'Success', 
        description: `Pincodes imported! Created: ${result.created || 0}, Cities: ${result.cities_created || 0}` 
      });
      
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSeedingPincodes(false);
      setSeedProgress('');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="location-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Location Settings</h1>
          <p className="text-gray-500 text-sm">Manage Indian postal database - States, Districts, Cities & Pincodes</p>
        </div>
        <Button 
          onClick={() => setSeederDialogOpen(true)} 
          variant="outline"
          className="gap-2"
          data-testid="open-seeder-btn"
        >
          <Download className="w-4 h-4" /> Import India Data
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">States</p>
                <p className="text-2xl font-bold">{stats.states}</p>
              </div>
              <Globe className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Districts</p>
                <p className="text-2xl font-bold">{stats.districts}</p>
              </div>
              <Map className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Cities</p>
                <p className="text-2xl font-bold">{stats.cities}</p>
              </div>
              <Building2 className="w-8 h-8 text-teal-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pincodes</p>
                <p className="text-2xl font-bold">{stats.pincodes}</p>
              </div>
              <Hash className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="states" className="data-[state=active]:bg-white">
            <Globe className="w-4 h-4 mr-2" /> States
          </TabsTrigger>
          <TabsTrigger value="districts" className="data-[state=active]:bg-white">
            <Map className="w-4 h-4 mr-2" /> Districts
          </TabsTrigger>
          <TabsTrigger value="cities" className="data-[state=active]:bg-white">
            <Building2 className="w-4 h-4 mr-2" /> Cities
          </TabsTrigger>
          <TabsTrigger value="pincodes" className="data-[state=active]:bg-white">
            <Hash className="w-4 h-4 mr-2" /> Pincodes
          </TabsTrigger>
        </TabsList>

        {/* States Tab */}
        <TabsContent value="states" className="mt-4 space-y-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search states..."
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-300"
                  />
                </div>
                <Button onClick={() => { setEditingState(null); setStateForm({ name: '', code: '', is_active: true }); setStateDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add State
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-700">Code</TableHead>
                    <TableHead className="text-gray-700">State Name</TableHead>
                    <TableHead className="text-gray-700">Status</TableHead>
                    <TableHead className="text-gray-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {states.map(state => (
                    <TableRow key={state.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-blue-600">{state.code}</TableCell>
                      <TableCell className="text-gray-800">{state.name}</TableCell>
                      <TableCell>
                        <Badge className={state.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {state.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditState(state)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => confirmDelete(state, 'state')} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Districts Tab */}
        <TabsContent value="districts" className="mt-4 space-y-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <Select value={selectedStateForDistrict} onValueChange={setSelectedStateForDistrict}>
                    <SelectTrigger className="w-48 bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Filter by State" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All States</SelectItem>
                      {statesDropdown.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search districts..."
                      value={districtSearch}
                      onChange={(e) => setDistrictSearch(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <Button onClick={() => { setEditingDistrict(null); setDistrictForm({ name: '', state_id: '', is_active: true }); setDistrictDialogOpen(true); }} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" /> Add District
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-700">District Name</TableHead>
                    <TableHead className="text-gray-700">State</TableHead>
                    <TableHead className="text-gray-700">Status</TableHead>
                    <TableHead className="text-gray-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {districts.map(district => (
                    <TableRow key={district.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-gray-800">{district.name}</TableCell>
                      <TableCell className="text-gray-600">{district.state_name} ({district.state_code})</TableCell>
                      <TableCell>
                        <Badge className={district.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {district.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditDistrict(district)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => confirmDelete(district, 'district')} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {districtTotal > 50 && (
                <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-500">Showing {districts.length} of {districtTotal} districts</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={districtPage <= 1} onClick={() => setDistrictPage(p => p - 1)}>Previous</Button>
                    <Button variant="outline" size="sm" disabled={districtPage * 50 >= districtTotal} onClick={() => setDistrictPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cities Tab */}
        <TabsContent value="cities" className="mt-4 space-y-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <Select value={selectedDistrictForCity} onValueChange={setSelectedDistrictForCity}>
                    <SelectTrigger className="w-48 bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Filter by District" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Districts</SelectItem>
                      {districtsDropdown.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search cities..."
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <Button onClick={() => { setEditingCity(null); setCityForm({ name: '', district_id: '', is_active: true }); setCityDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" /> Add City
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              {cities.length === 0 ? (
                <div className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No cities found. Add cities to districts first.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-700">City Name</TableHead>
                      <TableHead className="text-gray-700">District</TableHead>
                      <TableHead className="text-gray-700">State</TableHead>
                      <TableHead className="text-gray-700">Status</TableHead>
                      <TableHead className="text-gray-700 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cities.map(city => (
                      <TableRow key={city.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-gray-800">{city.name}</TableCell>
                        <TableCell className="text-gray-600">{city.district_name}</TableCell>
                        <TableCell className="text-gray-600">{city.state_name}</TableCell>
                        <TableCell>
                          <Badge className={city.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {city.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditCity(city)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmDelete(city, 'city')} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pincodes Tab */}
        <TabsContent value="pincodes" className="mt-4 space-y-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search pincode or area..."
                      value={pincodeSearch}
                      onChange={(e) => setPincodeSearch(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <Button onClick={() => { setEditingPincode(null); setPincodeForm({ pincode: '', area_name: '', city_id: '', is_active: true }); setPincodeDialogOpen(true); }} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Pincode
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              {pincodes.length === 0 ? (
                <div className="py-12 text-center">
                  <Hash className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No pincodes found. Add pincodes to cities first.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-700">Pincode</TableHead>
                      <TableHead className="text-gray-700">Area Name</TableHead>
                      <TableHead className="text-gray-700">City</TableHead>
                      <TableHead className="text-gray-700">District</TableHead>
                      <TableHead className="text-gray-700">State</TableHead>
                      <TableHead className="text-gray-700">Status</TableHead>
                      <TableHead className="text-gray-700 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pincodes.map(pin => (
                      <TableRow key={pin.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-amber-600">{pin.pincode}</TableCell>
                        <TableCell className="text-gray-800">{pin.area_name}</TableCell>
                        <TableCell className="text-gray-600">{pin.city_name}</TableCell>
                        <TableCell className="text-gray-600">{pin.district_name}</TableCell>
                        <TableCell className="text-gray-600">{pin.state_name}</TableCell>
                        <TableCell>
                          <Badge className={pin.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {pin.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditPincode(pin)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmDelete(pin, 'pincode')} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* State Dialog */}
      <Dialog open={stateDialogOpen} onOpenChange={setStateDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingState ? 'Edit State' : 'Add State'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>State Code *</Label>
              <Input
                value={stateForm.code}
                onChange={(e) => setStateForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g., TN, KA, MH"
                maxLength={3}
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label>State Name *</Label>
              <Input
                value={stateForm.name}
                onChange={(e) => setStateForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Tamil Nadu"
                className="bg-gray-50"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={stateForm.is_active} onCheckedChange={(v) => setStateForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveState} className="bg-blue-600 hover:bg-blue-700">
              {editingState ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* District Dialog */}
      <Dialog open={districtDialogOpen} onOpenChange={setDistrictDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDistrict ? 'Edit District' : 'Add District'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>State *</Label>
              <Select value={districtForm.state_id} onValueChange={(v) => setDistrictForm(p => ({ ...p, state_id: v }))}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {statesDropdown.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>District Name *</Label>
              <Input
                value={districtForm.name}
                onChange={(e) => setDistrictForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Chennai"
                className="bg-gray-50"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={districtForm.is_active} onCheckedChange={(v) => setDistrictForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistrictDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDistrict} className="bg-purple-600 hover:bg-purple-700">
              {editingDistrict ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* City Dialog */}
      <Dialog open={cityDialogOpen} onOpenChange={setCityDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCity ? 'Edit City' : 'Add City'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>State *</Label>
              <Select onValueChange={(v) => fetchDistrictsDropdown(v)}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select State First" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {statesDropdown.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>District *</Label>
              <Select value={cityForm.district_id} onValueChange={(v) => setCityForm(p => ({ ...p, district_id: v }))}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {districtsDropdown.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>City Name *</Label>
              <Input
                value={cityForm.name}
                onChange={(e) => setCityForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Tambaram"
                className="bg-gray-50"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={cityForm.is_active} onCheckedChange={(v) => setCityForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCityDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCity} className="bg-teal-600 hover:bg-teal-700">
              {editingCity ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pincode Dialog */}
      <Dialog open={pincodeDialogOpen} onOpenChange={setPincodeDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPincode ? 'Edit Pincode' : 'Add Pincode'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>State *</Label>
              <Select onValueChange={(v) => fetchDistrictsDropdown(v)}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select State First" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {statesDropdown.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>District *</Label>
              <Select onValueChange={(v) => fetchCitiesDropdown(v)}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {districtsDropdown.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <Select value={pincodeForm.city_id} onValueChange={(v) => setPincodeForm(p => ({ ...p, city_id: v }))}>
                <SelectTrigger className="bg-gray-50">
                  <SelectValue placeholder="Select City" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {citiesDropdown.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pincode *</Label>
                <Input
                  value={pincodeForm.pincode}
                  onChange={(e) => setPincodeForm(p => ({ ...p, pincode: e.target.value }))}
                  placeholder="e.g., 600045"
                  maxLength={6}
                  className="bg-gray-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Area Name *</Label>
                <Input
                  value={pincodeForm.area_name}
                  onChange={(e) => setPincodeForm(p => ({ ...p, area_name: e.target.value }))}
                  placeholder="e.g., Saidapet"
                  className="bg-gray-50"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={pincodeForm.is_active} onCheckedChange={(v) => setPincodeForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPincodeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePincode} className="bg-amber-600 hover:bg-amber-700">
              {editingPincode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deactivation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate this {deleteType}? This will mark it as inactive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Data Seeder Dialog */}
      <Dialog open={seederDialogOpen} onOpenChange={setSeederDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" /> Import India Location Data
            </DialogTitle>
            <DialogDescription>
              Seed complete Indian postal data including all states, districts, cities, and pincodes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Stats */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium mb-2">Current Data:</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.states}</p>
                  <p className="text-xs text-gray-500">States</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.districts}</p>
                  <p className="text-xs text-gray-500">Districts</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-teal-600">{stats.cities}</p>
                  <p className="text-xs text-gray-500">Cities</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.pincodes}</p>
                  <p className="text-xs text-gray-500">Pincodes</p>
                </div>
              </div>
            </div>

            {/* Step 1: States & Districts */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Step 1: States & Districts</p>
                    <p className="text-sm text-gray-500">36 States/UTs + 750 Districts</p>
                  </div>
                  <Button 
                    onClick={seedStatesAndDistricts} 
                    disabled={seedingStates || seedingPincodes}
                    size="sm"
                    data-testid="seed-states-btn"
                  >
                    {seedingStates ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Seeding...</>
                    ) : stats.states >= 36 && stats.districts >= 700 ? (
                      <><Check className="w-4 h-4 mr-2 text-green-500" /> Done</>
                    ) : (
                      'Seed States'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Pincodes */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Step 2: Cities & Pincodes</p>
                    <p className="text-sm text-gray-500">~33,000+ Pincodes from India Post data</p>
                  </div>
                  <Button 
                    onClick={seedAllPincodes} 
                    disabled={seedingStates || seedingPincodes || stats.districts < 100}
                    size="sm"
                    data-testid="seed-pincodes-btn"
                  >
                    {seedingPincodes ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                    ) : stats.pincodes >= 30000 ? (
                      <><Check className="w-4 h-4 mr-2 text-green-500" /> Done</>
                    ) : (
                      'Import Pincodes'
                    )}
                  </Button>
                </div>
                {stats.districts < 100 && (
                  <p className="text-xs text-amber-600 mt-2">⚠️ Seed States & Districts first</p>
                )}
              </CardContent>
            </Card>

            {/* Progress */}
            {seedProgress && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {seedProgress}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSeederDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationSettingsPage;
